import {
    JarvisPrice,
    JarvisPriceType,
    JarvisProduct,
} from '@vfde-sails/availability';
import { getCMSConfig } from '../cms';
import { CmsPlaceholder } from '../../enum';
import {
    NO_PATTERN_BUSINESS_LOGIC,
    Price,
} from '@vfde-brix/ws10/core';
import { DeepPartial } from '@vfde-brix/ws10/core/types/Utils';
import {
    IOfferSummaryCardAdditionalPrice,
    IOfferSummaryCardPromoPrice,
} from '@vfde-brix/ws10/offer-summary-card';
import {
    OfferSummaryCardConditionBefore,
    OfferSummaryCardLabel,
} from '../../enums/content';

/**
 * Gets the subline text for the Offer Price component
 */
export const getTariffSublineText = ({ download, upload }: JarvisProduct): string =>
    getCMSConfig().offerSummaryCard.tariffSubline.text
        .replace(CmsPlaceholder.Download, download.toString())
        .replace(CmsPlaceholder.Upload, upload.toString());

/**
 * Gets the additional texts for the Offer Price component
 */
export const getLegalTexts = (tariff: JarvisProduct): string[] => [
    OfferSummaryCardLabel.Duration.replace(CmsPlaceholder.Duration, tariff.duration.toString()),
    `<p><a href="${tariff.pdfLink}" class="ws10-text-link" target="_blank" rel="noopener noreferrer">${OfferSummaryCardLabel.PibLink}</a></p>`,
];

const getPricePropsByType = ({ type, value, stairway }: JarvisPrice): {
    label: string,
    price?: Price,
    conditionBefore?: string,
} => {
    switch (type) {
        case JarvisPriceType.Once:
            return {
                label: OfferSummaryCardLabel.PriceOnce,
                price: Price.fromNumber(value),
                conditionBefore: OfferSummaryCardConditionBefore.PriceOnce,
            };
        case JarvisPriceType.Monthly:
            return {
                label: OfferSummaryCardLabel.PriceMonthly,
                price: Price.fromNumber(value),
                conditionBefore: OfferSummaryCardConditionBefore.None,
            };
        case JarvisPriceType.Stairway:
            if (!stairway) {
                // Note: Invalid Jarvis price structure
                return { label: '' };
            }

            return {
                price: Price.fromNumber(stairway.value),
                label: OfferSummaryCardLabel.PriceMonthly,
                conditionBefore: OfferSummaryCardConditionBefore.None,
            };
    }
};

/**
 * Gets the price properties for a tariff
 */
export const getSummaryCardUpdatedPriceProps = (tariffPrice: JarvisPrice): DeepPartial<IOfferSummaryCardPromoPrice>[] => {
    const { label, price, conditionBefore } = getPricePropsByType(tariffPrice);

    return [{
        txtLabel: label,
        containerPromoPrice: {
            price,
            strikePrice: undefined,
            optPriceConditionBefore: conditionBefore,
            business: NO_PATTERN_BUSINESS_LOGIC,
        },
    }];
};

const getStairwayAdditionalPriceProps = (
    { type, value, stairway }: JarvisPrice,
): DeepPartial<IOfferSummaryCardAdditionalPrice> | null => (type === JarvisPriceType.Stairway && stairway) ? {
    txtLabel: OfferSummaryCardLabel.PriceStairway.replace(CmsPlaceholder.StairwayMonth, `${stairway.period + 1}`),
    price: Price.fromNumber(value),
} : null;

/**
 * Gets the additional price properties for a tariff
 */
export const getSummaryCardUpdatedAdditionalPriceProps = (
    tariffPrice: JarvisPrice,
): DeepPartial<IOfferSummaryCardAdditionalPrice>[] => {
    const stairway = getStairwayAdditionalPriceProps(tariffPrice);

    return [
        ...stairway ? [stairway] : [],
    ];
};
